import React, { useEffect, useMemo, useState } from 'react';
import { Video, Calendar, FileText, Clock, Link, Sparkles, CheckCircle, Zap, Folder, Presentation, Lightbulb, Plus, Trash2, Pencil } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { ConfirmDialog } from '../components/ui/Modal';
import {
  meetingPreparationService,
  type MeetingPrepSessionDetail,
  type MeetingPrepSessionSummary,
  type MeetingPrepTemplate,
  type UpdateMeetingPrepPayload,
} from '../services/meetingPreparation';
import { PageLayout } from '@/components/layout/PageLayout';

const MEETING_PREP_QUERY = ['meeting-prep-sessions'] as const;

function priorityDotClass(priority: string): string {
  switch (priority) {
    case 'high':
      return 'bg-red-500';
    case 'medium':
      return 'bg-yellow-500';
    case 'low':
      return 'bg-blue-500';
    default:
      return 'bg-khiimori-bg-secondary0';
  }
}

function priorityBadgeClass(priority: string): string {
  switch (priority) {
    case 'high':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    case 'medium':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    case 'low':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    default:
      return 'bg-khiimori-bg-elevated text-khiimori-text-secondary';
  }
}

function getResourceIcon(type: string): string {
  switch (type) {
    case 'document':
      return '📄';
    case 'link':
      return '🔗';
    case 'tab':
      return '🌐';
    case 'note':
      return '📝';
    default:
      return '📌';
  }
}

function formatTime(iso: string): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

function formatDateLabel(iso: string): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getTimeUntil(iso: string): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  if (diffMs < 0) return 'Past';
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  if (diffHours < 1) return `in ${diffMins}m`;
  if (diffHours < 24) return `in ${diffHours}h ${diffMins}m`;
  return formatDateLabel(iso);
}

function toDateTimeLocal(iso: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function detailToUpdatePayload(detail: MeetingPrepSessionDetail): UpdateMeetingPrepPayload {
  return {
    meetingTitle: detail.meetingTitle,
    attendees: detail.attendees,
    agenda: detail.agenda,
    platform: detail.platform ?? '',
    meetingUrl: detail.meetingUrl ?? '',
    durationMinutes: detail.durationMinutes && detail.durationMinutes > 0 ? detail.durationMinutes : 60,
    scheduledAt: detail.scheduledAt || undefined,
    notes: detail.notes ?? '',
  };
}

function sessionStatus(iso: string): 'upcoming' | 'in-progress' | 'completed' {
  if (!iso) return 'upcoming';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'upcoming';
  const now = Date.now();
  if (t < now - 60 * 60 * 1000) return 'completed';
  if (t < now) return 'in-progress';
  return 'upcoming';
}

function SectionHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3 min-h-[2.5rem]">
      <div className="min-w-0">
        <h2 className="text-base font-semibold text-khiimori-text-primary">{title}</h2>
        {description ? (
          <p className="mt-0.5 text-xs text-khiimori-text-muted">{description}</p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

const selectFieldClass =
  'w-full rounded-md border border-khiimori-border bg-khiimori-bg-elevated px-3 py-2 text-sm text-khiimori-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500';

const MeetingPreparation: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editAgenda, setEditAgenda] = useState('');
  const [editAttendees, setEditAttendees] = useState('');
  const [editPlatform, setEditPlatform] = useState('');
  const [editDuration, setEditDuration] = useState(60);
  const [editMeetingUrl, setEditMeetingUrl] = useState('');
  const [editScheduled, setEditScheduled] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [formTitle, setFormTitle] = useState('');
  const [formAgenda, setFormAgenda] = useState('');
  const [formAttendees, setFormAttendees] = useState('');
  const [formPlatform, setFormPlatform] = useState('');
  const [formDuration, setFormDuration] = useState(60);
  const [formMeetingUrl, setFormMeetingUrl] = useState('');
  const [formScheduled, setFormScheduled] = useState('');
  const [formTemplateId, setFormTemplateId] = useState('');

  const templatesQuery = useQuery({
    queryKey: [...MEETING_PREP_QUERY, 'templates'],
    queryFn: () => meetingPreparationService.listTemplates(),
  });
  const templates: MeetingPrepTemplate[] = templatesQuery.data ?? [];

  const sessionsQuery = useQuery({
    queryKey: MEETING_PREP_QUERY,
    queryFn: () => meetingPreparationService.listSessions(),
  });

  const sessions = sessionsQuery.data ?? [];

  useEffect(() => {
    if (!selectedId && sessions.length > 0) {
      setSelectedId(sessions[0].id);
    }
  }, [sessions, selectedId]);

  const detailQuery = useQuery({
    queryKey: [...MEETING_PREP_QUERY, 'detail', selectedId],
    queryFn: () => meetingPreparationService.getSession(selectedId as string),
    enabled: Boolean(selectedId),
  });

  const detail: MeetingPrepSessionDetail | undefined = detailQuery.data;

  const createMutation = useMutation({
    mutationFn: () =>
      meetingPreparationService.createSession({
        meetingTitle: formTitle.trim(),
        agenda: formAgenda.trim(),
        attendees: formAttendees
          .split(',')
          .map((a) => a.trim())
          .filter(Boolean),
        platform: formPlatform.trim() || undefined,
        durationMinutes: formDuration > 0 ? formDuration : undefined,
        meetingUrl: formMeetingUrl.trim() || undefined,
        scheduledAt: formScheduled ? new Date(formScheduled).toISOString() : undefined,
      }),
    onSuccess: async (row) => {
      setFormTitle('');
      setFormAgenda('');
      setFormAttendees('');
      setFormPlatform('');
      setFormDuration(60);
      setFormMeetingUrl('');
      setFormScheduled('');
      const templateId = formTemplateId;
      setFormTemplateId('');
      await queryClient.invalidateQueries({ queryKey: MEETING_PREP_QUERY });
      setSelectedId(row.id);
      try {
        if (templateId) {
          await meetingPreparationService.applyTemplate(row.id, templateId);
        }
        await meetingPreparationService.prepareSession(row.id);
        await queryClient.invalidateQueries({ queryKey: MEETING_PREP_QUERY });
        await queryClient.invalidateQueries({ queryKey: [...MEETING_PREP_QUERY, 'detail', row.id] });
        toast.success('Meeting session created and prepared');
      } catch {
        toast.success('Meeting session created — use Prepare to pull in context');
      }
    },
    onError: () => {
      toast.error('Failed to create meeting session');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => meetingPreparationService.deleteSession(id),
    onSuccess: async (_, id) => {
      if (selectedId === id) setSelectedId(null);
      await queryClient.invalidateQueries({ queryKey: MEETING_PREP_QUERY });
      await queryClient.invalidateQueries({ queryKey: [...MEETING_PREP_QUERY, 'detail', id] });
      toast.success('Meeting preparation session deleted');
    },
    onError: () => {
      toast.error('Failed to delete session');
    },
  });

  const prepareMutation = useMutation({
    mutationFn: (id: string) => meetingPreparationService.prepareSession(id),
    onSuccess: async (_, id) => {
      await queryClient.invalidateQueries({ queryKey: MEETING_PREP_QUERY });
      await queryClient.invalidateQueries({ queryKey: [...MEETING_PREP_QUERY, 'detail', id] });
      toast.success('Meeting prepared with suggested resources');
    },
    onError: () => {
      toast.error('Failed to prepare meeting');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateMeetingPrepPayload }) =>
      meetingPreparationService.updateSession(id, payload),
    onSuccess: async (_, { id }) => {
      await queryClient.invalidateQueries({ queryKey: MEETING_PREP_QUERY });
      await queryClient.invalidateQueries({ queryKey: [...MEETING_PREP_QUERY, 'detail', id] });
      toast.success('Meeting updated');
    },
    onError: () => {
      toast.error('Failed to update meeting');
    },
  });

  const openEditModal = (meeting: MeetingPrepSessionSummary | MeetingPrepSessionDetail) => {
    setEditTitle(meeting.meetingTitle);
    setEditAgenda(meeting.agenda ?? '');
    setEditAttendees(meeting.attendees.join(', '));
    setEditPlatform(meeting.platform ?? '');
    setEditDuration(meeting.durationMinutes && meeting.durationMinutes > 0 ? meeting.durationMinutes : 60);
    setEditMeetingUrl(meeting.meetingUrl ?? '');
    setEditScheduled(toDateTimeLocal(meeting.scheduledAt));
    setEditNotes(meeting.notes ?? '');
    setEditOpen(true);
  };

  const openNotesModal = () => {
    if (!detail) return;
    setNotesDraft(detail.notes ?? '');
    setNotesOpen(true);
  };

  const buildEditPayload = (): UpdateMeetingPrepPayload => ({
    meetingTitle: editTitle.trim(),
    agenda: editAgenda.trim(),
    attendees: editAttendees
      .split(',')
      .map((a) => a.trim())
      .filter(Boolean),
    platform: editPlatform.trim(),
    durationMinutes: editDuration > 0 ? editDuration : 60,
    meetingUrl: editMeetingUrl.trim(),
    scheduledAt: editScheduled ? new Date(editScheduled).toISOString() : undefined,
    notes: editNotes.trim(),
  });

  const saveEdit = () => {
    if (!selectedId || !editTitle.trim()) return;
    updateMutation.mutate(
      { id: selectedId, payload: buildEditPayload() },
      { onSuccess: () => setEditOpen(false) },
    );
  };

  const saveNotes = () => {
    if (!selectedId || !detail) return;
    updateMutation.mutate(
      {
        id: selectedId,
        payload: { ...detailToUpdatePayload(detail), notes: notesDraft.trim() },
      },
      { onSuccess: () => setNotesOpen(false) },
    );
  };

  const upcomingCount = useMemo(
    () => sessions.filter((s) => sessionStatus(s.scheduledAt) === 'upcoming').length,
    [sessions],
  );

  const todayCount = useMemo(
    () => sessions.filter((s) => formatDateLabel(s.scheduledAt) === 'Today').length,
    [sessions],
  );

  const resourceCount = detail?.resources.length ?? 0;
  const suggestionCount = detail?.suggestions.length ?? 0;
  const hoursSavedDisplay = useMemo(() => {
    const n = sessions.length;
    if (n === 0) return '0';
    const est = Math.round(n * 45) / 60;
    return est >= 1 ? `${est.toFixed(1)}h` : '<1h';
  }, [sessions.length]);

  const prepScore = detail?.preparationScore ?? 0;

  const firstPresentationTab = useMemo(() => {
    if (!detail?.tabs.length) return null;
    const pres = detail.tabs.find(
      (t) => /slides|presentation|deck|ppt/i.test(t.title) || t.purpose === 'presentation',
    );
    return pres ?? detail.tabs[0];
  }, [detail]);

  const errors = [
    sessionsQuery.error,
    detailQuery.error,
    createMutation.error,
    prepareMutation.error,
    deleteMutation.error,
    updateMutation.error,
  ].filter(Boolean);
  const errorMessage = errors.length > 0 ? String((errors[0] as Error).message) : '';

  return (
    <PageLayout title="Meeting Preparation" description="Automated meeting prep from your browsing context" icon={Calendar}>
      <div className="flex flex-col gap-4">
        {errorMessage ? (
          <p className="text-sm text-error-400" role="alert">
            {errorMessage}
          </p>
        ) : null}

        {/* Stats row */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[
            {
              label: 'Today',
              value: sessionsQuery.isLoading ? '…' : todayCount,
              icon: Calendar,
              wrap: 'bg-gradient-to-br from-success-500 to-success-600',
              valueClass:
                'text-xl font-bold leading-none bg-gradient-to-r from-success-500 to-success-600 bg-clip-text text-transparent',
            },
            {
              label: 'Resources',
              value: selectedId ? resourceCount : '—',
              icon: Folder,
              wrap: 'bg-gradient-to-br from-info-500 to-info-600',
              valueClass:
                'text-xl font-bold leading-none bg-gradient-to-r from-info-500 to-info-600 bg-clip-text text-transparent',
            },
            {
              label: 'Suggestions',
              value: selectedId ? suggestionCount : '—',
              icon: Lightbulb,
              wrap: 'bg-gradient-to-br from-warning-500 to-warning-600',
              valueClass:
                'text-xl font-bold leading-none bg-gradient-to-r from-warning-500 to-warning-600 bg-clip-text text-transparent',
            },
            {
              label: 'Time saved (est.)',
              value: hoursSavedDisplay,
              icon: Clock,
              wrap: 'bg-gradient-to-br from-primary-500 to-primary-600',
              valueClass:
                'text-xl font-bold leading-none bg-gradient-to-r from-primary-500 to-primary-600 bg-clip-text text-transparent',
            },
          ].map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.label} padding="sm" hoverable className="h-full">
                <div className="flex items-center gap-2.5">
                  <div className={`shrink-0 rounded-lg p-2 ${stat.wrap}`}>
                    <Icon className="h-4 w-4 text-khiimori-text-primary" />
                  </div>
                  <div className="min-w-0">
                    <div className={stat.valueClass}>{stat.value}</div>
                    <div className="text-[11px] text-khiimori-text-muted">{stat.label}</div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Main two-column workspace */}
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-12">
          {/* Left: form + meetings + prep score */}
          <div className="flex flex-col gap-4 lg:col-span-7">
            <Card padding="sm">
              <SectionHeader title="New meeting" />
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Input
                  label="Meeting title *"
                  type="text"
                  placeholder="e.g. Khiimori review"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                />
                <Input
                  label="Date & time"
                  type="datetime-local"
                  value={formScheduled}
                  onChange={(e) => setFormScheduled(e.target.value)}
                />
                <Input
                  label="Attendees"
                  type="text"
                  placeholder="Comma-separated"
                  value={formAttendees}
                  onChange={(e) => setFormAttendees(e.target.value)}
                />
                <Input
                  label="Platform"
                  type="text"
                  placeholder="Zoom, Meet…"
                  value={formPlatform}
                  onChange={(e) => setFormPlatform(e.target.value)}
                />
                <Input
                  label="Duration (min)"
                  type="number"
                  min={5}
                  value={formDuration}
                  onChange={(e) => setFormDuration(Number(e.target.value) || 60)}
                />
                <Input
                  label="Join URL"
                  type="url"
                  placeholder="https://…"
                  value={formMeetingUrl}
                  onChange={(e) => setFormMeetingUrl(e.target.value)}
                />
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-sm font-medium text-khiimori-text-primary">
                    Template
                  </label>
                  <select
                    value={formTemplateId}
                    onChange={(e) => setFormTemplateId(e.target.value)}
                    className={selectFieldClass}
                  >
                    <option value="">None — custom meeting</option>
                    {templates.map((tpl) => (
                      <option key={tpl.id} value={tpl.id}>
                        {tpl.name}
                      </option>
                    ))}
                  </select>
                </div>
                <Textarea
                  label="Agenda"
                  placeholder="What will you cover?"
                  value={formAgenda}
                  onChange={(e) => setFormAgenda(e.target.value)}
                  className="sm:col-span-2 min-h-[72px]"
                />
              </div>
              <div className="mt-3 flex items-center justify-between gap-3 border-t border-khiimori-border/10 pt-3">
                <span className="text-xs text-khiimori-text-muted">
                  {upcomingCount} upcoming · {sessions.length} total
                </span>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!formTitle.trim() || createMutation.isPending}
                  isLoading={createMutation.isPending}
                  onClick={() => createMutation.mutate()}
                  leftIcon={<Plus className="h-4 w-4" />}
                >
                  Create session
                </Button>
              </div>
            </Card>

            <div>
              <SectionHeader
                title="Upcoming meetings"
                description={sessions.length > 0 ? `${sessions.length} scheduled` : undefined}
              />
              <div className="mt-2 flex flex-col gap-2">
                {sessionsQuery.isLoading ? (
                  <Card padding="sm">
                    <p className="text-sm text-khiimori-text-muted">Loading…</p>
                  </Card>
                ) : null}
                {!sessionsQuery.isLoading && sessions.length === 0 ? (
                  <Card padding="sm">
                    <p className="text-sm text-khiimori-text-muted">No sessions yet — create one above.</p>
                  </Card>
                ) : null}
                {sessions.map((meeting: MeetingPrepSessionSummary) => (
                  <Card
                    key={meeting.id}
                    padding="sm"
                    hoverable
                    clickable
                    onClick={() => setSelectedId(meeting.id)}
                    className={selectedId === meeting.id ? 'ring-2 ring-primary-500/60' : ''}
                  >
                    <div className="flex items-center gap-3">
                      <div className="shrink-0 rounded-lg bg-gradient-to-br from-success-500 to-info-500 p-2">
                        <Video className="h-4 w-4 text-khiimori-text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="truncate text-sm font-semibold text-khiimori-text-primary">
                            {meeting.meetingTitle || 'Untitled'}
                          </h3>
                          <span className="shrink-0 rounded-full bg-success-500/20 px-2 py-0.5 text-[10px] font-medium text-success-400">
                            {getTimeUntil(meeting.scheduledAt)}
                          </span>
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-khiimori-text-muted">
                          <span>{formatDateLabel(meeting.scheduledAt)} · {formatTime(meeting.scheduledAt)}</span>
                          <span>{meeting.durationMinutes || 60}m</span>
                          <span>{meeting.attendees.length} attendees</span>
                          <span>{meeting.platform?.trim() || '—'}</span>
                        </div>
                        {meeting.attendees.length > 0 ? (
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {meeting.attendees.slice(0, 4).map((a, idx) => (
                              <span
                                key={`${meeting.id}-a-${idx}`}
                                className="rounded bg-khiimori-bg-elevated/60 px-1.5 py-0.5 text-[10px] text-khiimori-text-secondary"
                              >
                                {a}
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </div>
                      <div className="flex shrink-0 items-center gap-0.5">
                        <button
                          type="button"
                          title="Edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedId(meeting.id);
                            openEditModal(meeting);
                          }}
                          className="rounded p-1 text-khiimori-text-muted hover:bg-primary-500/20 hover:text-primary-400"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          title="Delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTargetId(meeting.id);
                          }}
                          className="rounded p-1 text-khiimori-text-muted hover:bg-error-500/20 hover:text-error-400"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                        <Button
                          variant="primary"
                          size="sm"
                          className="ml-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedId(meeting.id);
                            prepareMutation.mutate(meeting.id);
                          }}
                          disabled={prepareMutation.isPending}
                          isLoading={prepareMutation.isPending}
                          leftIcon={<Zap className="h-3.5 w-3.5" />}
                        >
                          Prepare
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            <Card
              padding="sm"
              className="border-success-500/25 bg-gradient-to-r from-success-500/15 to-info-500/15"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2.5">
                  <div className="shrink-0 rounded-lg bg-gradient-to-br from-success-500 to-info-500 p-2">
                    <CheckCircle className="h-4 w-4 text-khiimori-text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-khiimori-text-primary">Preparation score</p>
                    <p className="text-[11px] text-khiimori-text-muted">
                      {selectedId && detail
                        ? `For “${detail.meetingTitle}”`
                        : 'Select a meeting'}
                    </p>
                  </div>
                </div>
                <span className="shrink-0 text-2xl font-bold tabular-nums bg-gradient-to-r from-success-500 to-info-500 bg-clip-text text-transparent">
                  {selectedId && detail ? `${prepScore}%` : '—'}
                </span>
              </div>
            </Card>
          </div>

          {/* Right: suggestions + resources + actions */}
          <div className="flex flex-col gap-4 lg:col-span-5 lg:sticky lg:top-4">
            {selectedId && detailQuery.isLoading ? (
              <Card padding="sm">
                <p className="text-sm text-khiimori-text-muted">Loading details…</p>
              </Card>
            ) : null}

            {selectedId && detail ? (
              <>
                <Card padding="sm" className="flex flex-col">
                  <div className="mb-3 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 shrink-0 text-success-500" />
                    <h3 className="text-sm font-semibold text-khiimori-text-primary">AI suggestions</h3>
                  </div>
                  <div className="max-h-[280px] space-y-2 overflow-y-auto pr-0.5">
                    {detail.suggestions.map((suggestion) => (
                      <div
                        key={suggestion.id}
                        className="flex gap-2 rounded-lg border border-khiimori-border/10 bg-khiimori-bg-elevated/30 p-2.5"
                      >
                        <div
                          className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${priorityDotClass(suggestion.priority)}`}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs leading-relaxed text-khiimori-text-secondary">
                            {suggestion.text}
                          </p>
                          <div className="mt-1 flex items-center gap-1.5">
                            <span
                              className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${priorityBadgeClass(suggestion.priority)}`}
                            >
                              {suggestion.priority}
                            </span>
                            <span className="text-[10px] text-khiimori-text-muted">{suggestion.category}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                <Card padding="sm">
                  <div className="mb-3 flex items-center gap-2">
                    <Folder className="h-4 w-4 shrink-0 text-info-500" />
                    <h3 className="text-sm font-semibold text-khiimori-text-primary">Relevant resources</h3>
                  </div>
                  <div className="max-h-[200px] space-y-1.5 overflow-y-auto">
                    {detail.resources.length === 0 ? (
                      <p className="text-xs text-khiimori-text-muted">Use Prepare to add tabs.</p>
                    ) : null}
                    {detail.resources.map((resource) => (
                      <div
                        key={resource.id}
                        role={resource.url ? 'button' : undefined}
                        tabIndex={resource.url ? 0 : undefined}
                        onClick={() => {
                          if (resource.url) window.open(resource.url, '_blank', 'noopener,noreferrer');
                        }}
                        onKeyDown={(e) => {
                          if (resource.url && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault();
                            window.open(resource.url, '_blank', 'noopener,noreferrer');
                          }
                        }}
                        className={`flex items-center gap-2 rounded-lg p-2 ${
                          resource.url ? 'cursor-pointer hover:bg-khiimori-bg-elevated/50' : ''
                        }`}
                      >
                        <span className="shrink-0 text-base">{getResourceIcon(resource.type)}</span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-xs font-medium text-khiimori-text-primary">
                            {resource.title}
                          </p>
                          <div className="mt-1 flex items-center gap-1.5">
                            <div className="h-1 min-w-0 flex-1 rounded-full bg-khiimori-bg-elevated/50">
                              <div
                                className="h-full rounded-full bg-gradient-to-r from-info-500 to-primary-500"
                                style={{ width: `${resource.relevance}%` }}
                              />
                            </div>
                            <span className="shrink-0 text-[10px] tabular-nums text-khiimori-text-muted">
                              {resource.relevance}%
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                <div className="flex flex-wrap gap-2">
                  <Button variant="glass" size="sm" onClick={() => openEditModal(detail)} leftIcon={<Pencil className="h-3.5 w-3.5" />}>
                    Edit
                  </Button>
                  <Button variant="glass" size="sm" onClick={openNotesModal} leftIcon={<FileText className="h-3.5 w-3.5" />}>
                    Notes
                  </Button>
                  <Button
                    variant="glass"
                    size="sm"
                    disabled={!firstPresentationTab?.url}
                    onClick={() => firstPresentationTab?.url && window.open(firstPresentationTab.url, '_blank', 'noopener,noreferrer')}
                    leftIcon={<Presentation className="h-3.5 w-3.5" />}
                  >
                    Open tab
                  </Button>
                  <Button
                    variant="glass"
                    size="sm"
                    disabled={!detail.meetingUrl?.trim()}
                    onClick={() => detail.meetingUrl?.trim() && window.open(detail.meetingUrl.trim(), '_blank', 'noopener,noreferrer')}
                    leftIcon={<Link className="h-3.5 w-3.5" />}
                  >
                    Join
                  </Button>
                </div>
              </>
            ) : (
              <Card padding="sm" className="text-center">
                <p className="text-sm text-khiimori-text-muted">
                  Select a meeting to see AI suggestions and resources.
                </p>
              </Card>
            )}
          </div>
        </div>
      </div>

      {notesOpen && detail ? (
        <Modal
          isOpen={true}
          onClose={() => setNotesOpen(false)}
          title="Meeting notes"
          size="md"
        >
          <div className="space-y-3 text-sm">
            <div>
              <span className="font-semibold text-khiimori-text-primary">Agenda</span>
              <p className="mt-1 whitespace-pre-wrap text-khiimori-text-secondary">
                {detail.agenda || '—'}
              </p>
            </div>
            <div>
              <label htmlFor="meeting-notes" className="font-semibold text-khiimori-text-primary">
                Your notes
              </label>
              <Textarea
                id="meeting-notes"
                placeholder="Talking points, questions to ask, follow-ups…"
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                className="mt-1 min-h-[140px]"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="glass" onClick={() => setNotesOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={saveNotes}
              disabled={updateMutation.isPending}
              isLoading={updateMutation.isPending}
            >
              Save notes
            </Button>
          </div>
        </Modal>
      ) : null}

      {editOpen && selectedId ? (
        <Modal
          isOpen={true}
          onClose={() => setEditOpen(false)}
          title="Edit meeting"
          size="lg"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Meeting title *"
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
            />
            <Input
              label="Date & time"
              type="datetime-local"
              value={editScheduled}
              onChange={(e) => setEditScheduled(e.target.value)}
            />
            <Input
              label="Attendees"
              type="text"
              placeholder="Comma-separated names"
              value={editAttendees}
              onChange={(e) => setEditAttendees(e.target.value)}
            />
            <Input
              label="Platform"
              type="text"
              placeholder="Zoom, Meet, Teams…"
              value={editPlatform}
              onChange={(e) => setEditPlatform(e.target.value)}
            />
            <Input
              label="Duration (minutes)"
              type="number"
              min={5}
              value={editDuration}
              onChange={(e) => setEditDuration(Number(e.target.value) || 60)}
            />
            <Input
              label="Meeting join URL"
              type="url"
              value={editMeetingUrl}
              onChange={(e) => setEditMeetingUrl(e.target.value)}
            />
            <Textarea
              label="Agenda"
              value={editAgenda}
              onChange={(e) => setEditAgenda(e.target.value)}
              className="sm:col-span-2 min-h-[88px]"
            />
            <Textarea
              label="Notes"
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              className="sm:col-span-2 min-h-[100px]"
            />
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="glass" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={saveEdit}
              disabled={!editTitle.trim() || updateMutation.isPending}
              isLoading={updateMutation.isPending}
            >
              Save changes
            </Button>
          </div>
        </Modal>
      ) : null}

      <ConfirmDialog
        isOpen={deleteTargetId !== null}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={() => {
          if (deleteTargetId) {
            deleteMutation.mutate(deleteTargetId);
          }
          setDeleteTargetId(null);
        }}
        title="Delete session?"
        message="This meeting preparation session will be permanently deleted."
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </PageLayout>
  );
};

export default MeetingPreparation;
